import React, { useState, useCallback } from 'react';
import './style.css';
import ModalBtn from '../../molecules/modalButton/view';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';
import Table from '../../molecules/tablas/view';
import ButtonAction from '../../atoms/buttonAction/view';
import Chip from '../../atoms/chip/container';
import TooltipC from '../../atoms/tooltip/view';
import {
    BsShieldCheck,
    BsArrowRepeat,
    BsCheckCircle,
    BsExclamationTriangle,
    BsSlashCircle,
    BsCloudArrowDown,
    BsXCircle
} from 'react-icons/bs';
import {
    detectInvoicingGaps,
    linkGapInvoice,
    discardGapInvoice,
    voidGapConsecutive
} from '../../../usecases/documents-usecase';
import { dispatchCustomEvent } from '../../../utils/dispatch';

export default function InvoicingAuditModal({ onUpdate }) {
    const [isLoading, setIsLoading] = useState(false);
    const [paginatedGaps, setPaginatedGaps] = useState({
        data: [],
        page: 1,
        page_size: 6,
        total: 0,
    });
    const [summaryStats, setSummaryStats] = useState({
        totalGaps: 0,
        totalEmitted: 0,
        totalError: 0,
        totalNotFound: 0,
    });
    const [hasScanned, setHasScanned] = useState(false);
    const [selectedGap, setSelectedGap] = useState(null);

    const uid = localStorage.getItem('uid') || '';
    const cid = localStorage.getItem('cid') || null;

    const runScan = useCallback(async (page = 1, limit = paginatedGaps.page_size || 6) => {
        setIsLoading(true);
        try {
            const res = await detectInvoicingGaps(uid, cid, undefined, page, limit);
            const data = res?.data || res || {};
            const items = data.gaps || [];
            const total = data.total !== undefined ? data.total : (data.total_gaps || items.length);

            setPaginatedGaps({
                data: items,
                page: data.page || page,
                page_size: data.page_size || limit,
                total: total,
            });

            setSummaryStats({
                totalGaps: total,
                totalEmitted: items.filter(g => g.status === 'FOUND_EMITTED').length,
                totalError: items.filter(g => g.status === 'FOUND_ERROR').length,
                totalNotFound: items.filter(g => g.status === 'NOT_FOUND').length,
            });
            setHasScanned(true);
        } catch (error) {
            console.error('Error al detectar huecos:', error);
            const msg = error?.response?.data?.message || error?.message || 'Error al escanear consecutivos';
            dispatchCustomEvent('popup', { message: msg, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [uid, cid, paginatedGaps.page_size]);

    const removeResolvedGap = useCallback((prefix, number, status) => {
        setPaginatedGaps(prev => {
            const updatedData = (prev.data || []).filter(g => !(g.prefix === prefix && g.number === number));
            const newTotal = Math.max(0, (prev.total || 0) - 1);
            return {
                ...prev,
                data: updatedData,
                total: newTotal,
            };
        });

        setSummaryStats(prev => ({
            ...prev,
            totalGaps: Math.max(0, prev.totalGaps - 1),
            totalEmitted: status === 'FOUND_EMITTED' ? Math.max(0, prev.totalEmitted - 1) : prev.totalEmitted,
            totalError: status === 'FOUND_ERROR' ? Math.max(0, prev.totalError - 1) : prev.totalError,
            totalNotFound: status === 'NOT_FOUND' ? Math.max(0, prev.totalNotFound - 1) : prev.totalNotFound,
        }));
    }, []);

    const handleSyncClick = (gap) => {
        setSelectedGap(gap);
        dispatchCustomEvent('notification-confirm-audit-link-gap', gap);
    };

    const handleConfirmSync = async (gapData) => {
        const gap = (gapData && gapData.prefix) ? gapData : (gapData && gapData.data && gapData.data.prefix ? gapData.data : selectedGap);
        if (!gap || !gap.prefix || gap.number === undefined) return;

        setIsLoading(true);
        try {
            // Si ya tiene asiento contable detectado, registramos el documento electrónico sin duplicar contabilidad
            if (gap.has_accounting) {
                const res = await discardGapInvoice(uid, cid, gap.prefix, gap.number);
                dispatchCustomEvent('popup', {
                    message: res?.message || `Factura ${gap.prefix}-${gap.number} sincronizada exitosamente (asiento contable previo preservado)`,
                    type: 'success'
                });
            } else {
                // Si no tiene asiento, registramos y generamos el asiento contable correspondiente
                const res = await linkGapInvoice(uid, cid, gap.prefix, gap.number);
                dispatchCustomEvent('popup', {
                    message: res?.message || `Factura ${gap.prefix}-${gap.number} sincronizada y contabilizada exitosamente`,
                    type: 'success'
                });
            }
            removeResolvedGap(gap.prefix, gap.number, gap.status);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Error sincronizando factura:', error);
            const msg = error?.response?.data?.message || error?.message || 'Error al sincronizar factura';
            dispatchCustomEvent('popup', { message: msg, type: 'error' });
        } finally {
            setIsLoading(false);
            setSelectedGap(null);
        }
    };

    const handleDiscardClick = (gap) => {
        setSelectedGap(gap);
        dispatchCustomEvent('notification-confirm-audit-discard-gap', gap);
    };

    const handleConfirmDiscard = async (gapData) => {
        const gap = (gapData && gapData.prefix) ? gapData : (gapData && gapData.data && gapData.data.prefix ? gapData.data : selectedGap);
        if (!gap || !gap.prefix || gap.number === undefined) return;

        setIsLoading(true);
        try {
            const res = await discardGapInvoice(uid, cid, gap.prefix, gap.number);
            dispatchCustomEvent('popup', {
                message: res?.message || `Factura ${gap.prefix}-${gap.number} guardada sin generar asiento contable`,
                type: 'success'
            });
            removeResolvedGap(gap.prefix, gap.number, gap.status);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Error descartando factura:', error);
            const msg = error?.response?.data?.message || error?.message || 'Error al descartar factura';
            dispatchCustomEvent('popup', { message: msg, type: 'error' });
        } finally {
            setIsLoading(false);
            setSelectedGap(null);
        }
    };

    const handleVoidClick = (gap) => {
        setSelectedGap(gap);
        dispatchCustomEvent('notification-confirm-audit-void-gap', gap);
    };

    const handleConfirmVoid = async (gapData) => {
        const gap = (gapData && gapData.prefix) ? gapData : (gapData && gapData.data && gapData.data.prefix ? gapData.data : selectedGap);
        if (!gap || !gap.prefix || gap.number === undefined) return;

        setIsLoading(true);
        try {
            const res = await voidGapConsecutive(uid, cid, gap.prefix, gap.number, 'Consecutivo inutilizado por auditoría');
            dispatchCustomEvent('popup', { message: res?.message || 'Consecutivo inutilizado', type: 'success' });
            removeResolvedGap(gap.prefix, gap.number, gap.status);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Error inutilizando consecutivo:', error);
            const msg = error?.response?.data?.message || error?.message || 'Error al registrar como inutilizado';
            dispatchCustomEvent('popup', { message: msg, type: 'error' });
        } finally {
            setIsLoading(false);
            setSelectedGap(null);
        }
    };

    const mappedTableData = (paginatedGaps.data || []).map(gap => {
        let badgeType = 'disable';
        let badgeLabel = 'No Encontrada';
        if (gap.status === 'FOUND_EMITTED') {
            badgeType = 'success';
            badgeLabel = 'Emitida DIAN';
        } else if (gap.status === 'FOUND_ERROR') {
            badgeType = 'error';
            badgeLabel = 'Error DIAN';
        }

        return {
            consecutive: `${gap.prefix}-${gap.number}`,
            status_dian: gap.error_details ? (
                <TooltipC text={gap.error_details}>
                    <Chip type={badgeType}>{badgeLabel}</Chip>
                </TooltipC>
            ) : (
                <Chip type={badgeType}>{badgeLabel}</Chip>
            ),
            date: gap.date || '---',
            value: gap.total || 0,
            customer: gap.customer || '---',
            accounting: gap.has_accounting ? (
                <Chip type="success">Contabilizada</Chip>
            ) : (
                <Chip type="disable">Sin comprobante</Chip>
            ),
            _original: gap
        };
    });

    return (
        <>
            <ConfirmPopUp
                eventKey="audit-link-gap"
                msg={
                    selectedGap
                        ? selectedGap.has_accounting
                            ? `¿Seguro que deseas sincronizar la factura ${selectedGap.prefix}-${selectedGap.number}? Se detectó un asiento contable previo, por lo que se registrará el documento electrónico en EasyPOS preservando la contabilidad existente.`
                            : `¿Seguro que deseas sincronizar la factura ${selectedGap.prefix}-${selectedGap.number}? Se guardará el registro electrónico en EasyPOS y se creará el comprobante contable (FV) correspondiente.`
                        : '¿Seguro que deseas sincronizar esta factura?'
                }
                onAcept={handleConfirmSync}
            />
            <ConfirmPopUp
                eventKey="audit-discard-gap"
                msg={
                    selectedGap
                        ? `¿Seguro que deseas descartar la factura ${selectedGap.prefix}-${selectedGap.number}? Se guardará el registro electrónico en EasyPOS para cubrir el consecutivo fiscal, pero NO se generará ningún asiento contable.`
                        : '¿Seguro que deseas descartar esta factura sin generar asiento contable?'
                }
                onAcept={handleConfirmDiscard}
            />
            <ConfirmPopUp
                eventKey="audit-void-gap"
                msg={
                    selectedGap
                        ? `¿Deseas marcar el consecutivo ${selectedGap.prefix}-${selectedGap.number} como inutilizado localmente?`
                        : '¿Deseas marcar este consecutivo como inutilizado?'
                }
                onAcept={handleConfirmVoid}
            />
            <ModalBtn
                id="invoicing-audit-modal"
                title="Auditar Consecutivos"
                icon={<BsShieldCheck size={18} />}
                type="basic"
                style={{ width: '92vw', maxWidth: '1600px' }}
                onClick={() => {
                    if (!hasScanned) {
                        runScan(1, paginatedGaps.page_size);
                    }
                }}
            >
                <div className="invoicing-audit-container">
                    <div className="invoicing-audit-header">
                        <div>
                            <h2 className="invoicing-audit-title">
                                <BsShieldCheck size={24} /> Auditoría de Consecutivos y Huecos
                            </h2>
                            <p className="invoicing-audit-subtitle">
                                Detecta saltos en la numeración de facturas electrónicas, consulta su estado en Plemsi/DIAN y concílialos.
                            </p>
                        </div>
                        <button
                            className="audit-btn audit-btn-primary"
                            onClick={() => runScan(1, paginatedGaps.page_size)}
                            disabled={isLoading}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <BsArrowRepeat className={isLoading ? 'spin-anim' : ''} size={16} />
                            {isLoading ? 'Analizando...' : 'Re-escanear'}
                        </button>
                    </div>

                    {hasScanned && (
                        <div className="invoicing-audit-stats">
                            <div className="audit-stat-card">
                                <span className="audit-stat-label">Total Huecos</span>
                                <span className="audit-stat-value">{summaryStats.totalGaps}</span>
                            </div>
                            <div className="audit-stat-card stat-emitted">
                                <span className="audit-stat-label">Emitidas en DIAN</span>
                                <span className="audit-stat-value">{summaryStats.totalEmitted}</span>
                            </div>
                            <div className="audit-stat-card stat-error">
                                <span className="audit-stat-label">Errores en DIAN</span>
                                <span className="audit-stat-value">{summaryStats.totalError}</span>
                            </div>
                            <div className="audit-stat-card stat-notfound">
                                <span className="audit-stat-label">No Encontradas</span>
                                <span className="audit-stat-value">{summaryStats.totalNotFound}</span>
                            </div>
                        </div>
                    )}

                    <div className="invoicing-audit-table-container">
                        <Table
                            id="invoicing-audit-paginated-table"
                            headersList={["Consecutivo", "Estado DIAN", "Fecha", "Valor", "Cliente", "Contabilidad"]}
                            paginatedData={{
                                ...paginatedGaps,
                                data: mappedTableData
                            }}
                            getFunction={(page, limit) => runScan(page, limit)}
                            onReload={() => runScan(paginatedGaps.page, paginatedGaps.page_size)}
                            excludeList={["_original"]}
                            isLoading={isLoading}
                            allowPageSizeControl={true}
                            allowSearch={false}
                            formats={{
                                value: { type: 'money' },
                                date: { type: 'timestamp' }
                            }}
                        >
                            <ButtonAction
                                title={<BsCloudArrowDown size={17} />}
                                btnKey="audit-sync-btn"
                                tooltip={(row) => {
                                    const item = row._original || row;
                                    return item.has_accounting
                                        ? "Sincronizar (asociar documento preservando contabilidad previa)"
                                        : "Sincronizar y generar comprobante contable (FV)";
                                }}
                                onClick={(row) => handleSyncClick(row._original || row)}
                                hidden={(row) => {
                                    const item = row._original || row;
                                    return item.status !== 'FOUND_EMITTED';
                                }}
                            />
                            <ButtonAction
                                title={<BsXCircle size={17} />}
                                btnKey="audit-discard-btn"
                                tooltip="Descartar (guardar en EasyPOS sin generar asiento contable)"
                                onClick={(row) => handleDiscardClick(row._original || row)}
                                hidden={(row) => {
                                    const item = row._original || row;
                                    return item.status !== 'FOUND_EMITTED' || item.has_accounting;
                                }}
                            />
                            <ButtonAction
                                title={<BsSlashCircle size={17} />}
                                btnKey="audit-void-btn"
                                tooltip="Marcar consecutivo como inutilizado / anulado localmente"
                                onClick={(row) => handleVoidClick(row._original || row)}
                                hidden={(row) => {
                                    const item = row._original || row;
                                    return item.status === 'FOUND_EMITTED';
                                }}
                            />
                        </Table>
                    </div>
                </div>
            </ModalBtn>
        </>
    );
}

